import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, Star, Upload, X, Pencil } from "lucide-react";
import { API, mediaUrl } from "../lib/api";

const api = (path, opts = {}) =>
  fetch(`${API}${path}`, { credentials: "include", ...opts }).then(async (r) => {
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || "Errore"); }
    return r.json();
  });

const uploadMedia = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  return api("/admin/media", { method: "POST", body: fd });
};

const CATEGORIES = ["AI Governance", "Enterprise Risk", "Digital Transformation", "Board & Governance", "Leadership"];
const inputCls = "w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]";

function useCollection(kind, onCount) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api(`/admin/${kind}`); setItems(d.items || []); onCount?.(d.items.length); }
    catch (e) { toast.error(e.message); } setLoading(false);
  }, [kind, onCount]);
  useEffect(() => { load(); }, [load]);
  return { items, loading, load };
}

const emptyInsight = { title: "", subtitle: "", category: CATEGORIES[0], tags: [], date: "", external_link: "", body: "", cover_file_id: null, featured: false, status: "draft" };
const emptySpeaking = { event_name: "", organizer: "", date: "", location: "", role: "", talk_title: "", description: "", official_link: "", video_link: "", post_comment: "", photo_file_id: null, attachments: [], featured: false, status: "upcoming" };

export function AdminInsights({ onCount }) {
  const { items, loading, load } = useCollection("insights", onCount);
  const [editing, setEditing] = useState(null);

  const save = async (data) => {
    const payload = { ...data, tags: Array.isArray(data.tags) ? data.tags : String(data.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (data.id) await api(`/admin/insights/${data.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      else await api("/admin/insights", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error(e.message); }
  };
  const act = async (id, action) => {
    try { await api(`/admin/insights/${id}${action}`, { method: action === "" ? "DELETE" : "POST" }); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-insights">
      <button data-testid="new-insight" onClick={() => setEditing({ ...emptyInsight })} className="btn-fill inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-6 py-3 mb-6"><Plus size={14} /> Nuovo insight</button>
      {editing && <InsightForm data={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {loading ? <Center /> : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`insight-row-${it.id}`} className="bg-white border border-[var(--line)] p-4 flex items-center gap-4">
              {it.cover_file_id && <img src={mediaUrl(it.cover_file_id)} alt="" className="w-16 h-16 object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {it.featured && <Star size={14} className="text-[var(--gold)] fill-[var(--gold)]" />}
                  <span className="font-serif text-lg text-[var(--ink)] truncate">{it.title || "(senza titolo)"}</span>
                  <StatusPill on={it.status === "published"} labels={["Pubblicato", "Bozza"]} />
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-1">{it.category} · {it.date || "—"}</div>
              </div>
              <RowActions
                onEdit={() => setEditing({ ...emptyInsight, ...it, tags: (it.tags || []).join(", ") })}
                onDup={() => act(it.id, "/duplicate")}
                onDel={() => window.confirm("Eliminare?") && act(it.id, "")}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightForm({ data, onSave, onCancel }) {
  const [f, setF] = useState(data);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upCover = async (e) => { const file = e.target.files?.[0]; if (!file) return; setBusy(true); try { const m = await uploadMedia(file); setF({ ...f, cover_file_id: m.file_id }); } catch (err) { toast.error(err.message); } setBusy(false); };
  return (
    <div className="bg-white border border-[var(--petrol)] p-6 mb-6 space-y-3" data-testid="insight-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="if-title" placeholder="Titolo*" value={f.title} onChange={set("title")} className={inputCls} />
        <select data-testid="if-category" value={f.category} onChange={set("category")} className={inputCls}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
      </div>
      <input placeholder="Sottotitolo / abstract" value={f.subtitle} onChange={set("subtitle")} className={inputCls} />
      <div className="grid sm:grid-cols-2 gap-3">
        <input type="date" value={f.date || ""} onChange={set("date")} className={inputCls} />
        <input placeholder="Tag (separati da virgola)" value={f.tags} onChange={set("tags")} className={inputCls} />
      </div>
      <input placeholder="Link esterno (facolt.)" value={f.external_link || ""} onChange={set("external_link")} className={inputCls} />
      <textarea data-testid="if-body" placeholder="Testo" value={f.body} onChange={set("body")} rows={6} className={`${inputCls} resize-none`} />
      <div className="flex flex-wrap items-center gap-4">
        <label className="btn-fill inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] border border-[var(--petrol)] text-[var(--petrol)] hover:text-white px-4 py-2 cursor-pointer"><Upload size={14} /> Copertina<input type="file" accept="image/*" className="hidden" onChange={upCover} /></label>
        {f.cover_file_id && <img src={mediaUrl(f.cover_file_id)} alt="" className="h-12 w-20 object-cover" />}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> In evidenza</label>
        <select data-testid="if-status" value={f.status} onChange={set("status")} className="border border-[var(--line)] px-3 py-2 text-sm"><option value="draft">Bozza</option><option value="published">Pubblicato</option></select>
      </div>
      <div className="flex gap-3 pt-2">
        <button data-testid="if-save" disabled={busy} onClick={() => onSave(f)} className="btn-fill text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-6 py-3">Salva</button>
        <button onClick={onCancel} className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)] px-4 py-3">Annulla</button>
      </div>
    </div>
  );
}

export function AdminSpeaking({ onCount }) {
  const { items, loading, load } = useCollection("speaking", onCount);
  const [editing, setEditing] = useState(null);
  const save = async (data) => {
    try {
      if (data.id) await api(`/admin/speaking/${data.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      else await api("/admin/speaking", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error(e.message); }
  };
  const act = async (id, action) => { try { await api(`/admin/speaking/${id}${action}`, { method: action === "" ? "DELETE" : "POST" }); load(); } catch (e) { toast.error(e.message); } };

  return (
    <div data-testid="admin-speaking">
      <button data-testid="new-speaking" onClick={() => setEditing({ ...emptySpeaking })} className="btn-fill inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-6 py-3 mb-6"><Plus size={14} /> Nuovo evento</button>
      {editing && <SpeakingForm data={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {loading ? <Center /> : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`speaking-row-${it.id}`} className="bg-white border border-[var(--line)] p-4 flex items-center gap-4">
              {it.photo_file_id && <img src={mediaUrl(it.photo_file_id)} alt="" className="w-16 h-16 object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {it.featured && <Star size={14} className="text-[var(--gold)] fill-[var(--gold)]" />}
                  <span className="font-serif text-lg text-[var(--ink)] truncate">{it.event_name || "(senza nome)"}</span>
                  <StatusPill on={it.status === "upcoming"} labels={["Upcoming", "Past"]} />
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-1">{it.location || "—"} · {it.date || "—"} · {it.role}</div>
              </div>
              <RowActions
                onEdit={() => setEditing({ ...emptySpeaking, ...it })}
                onDup={() => act(it.id, "/duplicate")}
                onDel={() => window.confirm("Eliminare?") && act(it.id, "")}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SpeakingForm({ data, onSave, onCancel }) {
  const [f, setF] = useState({ ...data, attachments: data.attachments || [] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upPhoto = async (e) => { const file = e.target.files?.[0]; if (!file) return; setBusy(true); try { const m = await uploadMedia(file); setF({ ...f, photo_file_id: m.file_id }); } catch (err) { toast.error(err.message); } setBusy(false); };
  const upAtt = async (e) => { const file = e.target.files?.[0]; if (!file) return; setBusy(true); try { const m = await uploadMedia(file); setF((p) => ({ ...p, attachments: [...p.attachments, { file_id: m.file_id, filename: m.filename }] })); } catch (err) { toast.error(err.message); } setBusy(false); e.target.value = ""; };
  const rmAtt = (id) => setF((p) => ({ ...p, attachments: p.attachments.filter((a) => a.file_id !== id) }));
  return (
    <div className="bg-white border border-[var(--petrol)] p-6 mb-6 space-y-3" data-testid="speaking-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="sf-event" placeholder="Nome evento*" value={f.event_name} onChange={set("event_name")} className={inputCls} />
        <input placeholder="Organizzatore" value={f.organizer} onChange={set("organizer")} className={inputCls} />
        <input type="date" value={f.date || ""} onChange={set("date")} className={inputCls} />
        <input placeholder="Luogo" value={f.location} onChange={set("location")} className={inputCls} />
        <input placeholder="Ruolo (es. Keynote Speaker)" value={f.role} onChange={set("role")} className={inputCls} />
        <input placeholder="Titolo intervento" value={f.talk_title} onChange={set("talk_title")} className={inputCls} />
      </div>
      <textarea placeholder="Descrizione" value={f.description} onChange={set("description")} rows={3} className={`${inputCls} resize-none`} />
      <div className="grid sm:grid-cols-2 gap-3">
        <input placeholder="Link ufficiale" value={f.official_link || ""} onChange={set("official_link")} className={inputCls} />
        <input placeholder="Link video" value={f.video_link || ""} onChange={set("video_link")} className={inputCls} />
      </div>
      <textarea placeholder="Commento post-evento" value={f.post_comment} onChange={set("post_comment")} rows={2} className={`${inputCls} resize-none`} />
      <div className="flex flex-wrap items-center gap-4">
        <label className="btn-fill inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] border border-[var(--petrol)] text-[var(--petrol)] hover:text-white px-4 py-2 cursor-pointer"><Upload size={14} /> Foto<input type="file" accept="image/*" className="hidden" onChange={upPhoto} /></label>
        {f.photo_file_id && <img src={mediaUrl(f.photo_file_id)} alt="" className="h-12 w-20 object-cover" />}
        <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[var(--accent)] cursor-pointer"><Upload size={14} /> Allegato<input type="file" className="hidden" onChange={upAtt} /></label>
      </div>
      {f.attachments.length > 0 && <ul className="text-sm space-y-1">{f.attachments.map((a) => <li key={a.file_id} className="flex items-center gap-2 text-[var(--ink-soft)]">{a.filename} <button onClick={() => rmAtt(a.file_id)}><X size={13} /></button></li>)}</ul>}
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> In evidenza</label>
        <select data-testid="sf-status" value={f.status} onChange={set("status")} className="border border-[var(--line)] px-3 py-2 text-sm"><option value="upcoming">Upcoming</option><option value="past">Past</option></select>
      </div>
      <div className="flex gap-3 pt-2">
        <button data-testid="sf-save" disabled={busy} onClick={() => onSave(f)} className="btn-fill text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-6 py-3">Salva</button>
        <button onClick={onCancel} className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)] px-4 py-3">Annulla</button>
      </div>
    </div>
  );
}

const Center = () => <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[var(--petrol)]" /></div>;
const StatusPill = ({ on, labels }) => <span className={`text-[0.6rem] uppercase tracking-[0.15em] px-2 py-0.5 ${on ? "bg-[var(--petrol)] text-white" : "bg-[var(--line)] text-[var(--ink-soft)]"}`}>{on ? labels[0] : labels[1]}</span>;
const RowActions = ({ onEdit, onDup, onDel }) => (
  <div className="flex items-center gap-1 shrink-0">
    <button onClick={onEdit} className="p-2 text-[var(--ink-soft)] hover:text-[var(--petrol)]" title="Modifica"><Pencil size={15} /></button>
    <button onClick={onDup} className="p-2 text-[var(--ink-soft)] hover:text-[var(--petrol)]" title="Duplica"><Copy size={15} /></button>
    <button onClick={onDel} className="p-2 text-[var(--ink-soft)] hover:text-red-600" title="Elimina"><Trash2 size={15} /></button>
  </div>
);
